import {
    useState,
    useEffect,
    ChangeEventHandler,
    KeyboardEventHandler,
} from 'react';
import { useStore } from '@ui/hooks/useStore';
import useKeyDown from '@ui/hooks/useKeyDown';
import { ICloseEvent } from '@core/interfaces/ICloseEvent';
import { IState } from './IState';

const useAddGroup = (onClose: ICloseEvent['onClose']) => {
    const { groups, app, lang } = useStore();
    const key = useKeyDown();

    const initState: IState = {
        groupName: '',
        groupNameError: false,
    };
    const [state, setState] = useState(initState);

    const checkGroupNameExist = (name: string) => {
        return groups.groupsList.some((group) => group.name === name);
    };
    const groupNameValidate = (name: string) => {
        if (!name || checkGroupNameExist(name)) {
            setState((prev) => ({
                ...prev,
                groupNameError: true,
            }));
            return false;
        } else {
            setState((prev) => ({
                ...prev,
                groupNameError: false,
            }));
            return true;
        }
    };
    const handleInputClick = () => {
        groups.resetServerError();
        app.resetDemoModeError();
        setState((prev) => ({
            ...prev,
            groupNameError: false,
        }));
    };
    const handleGroupNameChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        handleInputClick();
        const groupName = e.target.value;
        setState((prev) => ({
            ...prev,
            groupName,
        }));
    };
    const handleCreateClick = () => {
        handleInputClick();
        groups.resetServerError();
        const { groupName } = state;
        const isGroupNameValid = groupNameValidate(groupName);
        if (isGroupNameValid && !app.demoMode) {
            groups.createGroup(groupName);
        } else if (app.demoMode) app.setDemoModeError();
    };
    const handleKey = (key: string) => {
        switch (key) {
            case 'Escape':
                onClose();
                break;
            case 'Enter':
                handleCreateClick();
                break;
        }
    };
    const handleKeyDown: KeyboardEventHandler<HTMLElement> = (e) => {
        const { key } = e;
        if (key === 'Enter') {
            e.stopPropagation();
            e.preventDefault();
            handleKey(key);
        }
    };
    const handleBtnKeyDown: KeyboardEventHandler<HTMLButtonElement> = (e) => {
        const { key } = e;
        if (key === 'Enter') {
            e.stopPropagation();
            e.preventDefault();
            const target = e.target as HTMLButtonElement;
            target.click();
        }
    };
    const handlers = {
        handleGroupNameChange,
        handleCreateClick,
        handleInputClick,
        handleKeyDown,
        handleBtnKeyDown,
    };

    useEffect(() => {
        handleInputClick();
        const element = document.activeElement as HTMLElement;
        if (groups.operationSuccess) {
            groups.resetOperationSuccess();
        }
        if (element) {
            element.blur();
        }
        return () => {
            handleInputClick();
        };
    }, []);
    useEffect(() => {
        if (groups.operationSuccess) {
            onClose();
            groups.resetOperationSuccess();
        }
    }, [groups.operationSuccess]);
    useEffect(() => {
        handleKey(key);
    }, [key]);

    return {
        store: groups,
        app,
        lang,
        state,
        handlers,
    };
};

export default useAddGroup;
